import React, { useMemo } from 'react';
import moment from 'moment';

type BankReconTableProps = {
    rows?: any[];
    type?: any;
    paginationData?: any;
    paginationState?: any;
    searchQuery?: any;
    setPaginationState?: (v: any) => void;
    loading?: boolean;
    showOnlyNonZeroVar?: boolean;
};

const SkeletonCell: React.FC<{ className?: string }> = ({ className = '' }) => (
    <div className={`h-4 w-full rounded bg-gray-200 dark:bg-[#1E1E1E] ${className}`} />
);

const SkeletonRow: React.FC = () => (
    <tr className='animate-pulse border border-1'>
        <td className='px-4 py-4'>
            <SkeletonCell className='max-w-[110px]' />
        </td>
        <td className='px-4 py-4'>
            <SkeletonCell className='max-w-[90px]' />
        </td>
        <td className='px-4 py-4'>
            <SkeletonCell className='max-w-[90px]' />
        </td>
        <td className='px-4 py-4'>
            <SkeletonCell className='max-w-[90px]' />
        </td>
    </tr>
);

const DATE_FORMATS = [
    'YYYY-MM-DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'MM/DD/YYYY',
    'DD-MM', 'DD/MM', 'MMM D YYYY', 'MMMM D YYYY', 'MMM YYYY', 'MMMM YYYY'
];

const looksLikeLooseDigits = (s: string) =>
    /^\d+([.,/-]\d+)*$/.test(s) && s.replace(/[^\d]/g, '').length <= 6;

const parseLooseNumber = (raw: string): number | null => {
    if (!raw) return null;
    const s = raw.trim();

    const commaAsDecimal = /,\d{1,2}$/.test(s);
    let normalized = s;

    if (commaAsDecimal) {
        normalized = s.replace(/\./g, '').replace(',', '.');
    } else {
        normalized = s.replace(/,/g, '');
    }

    const n = Number(normalized);
    return Number.isFinite(n) ? n : null;
};

const parseLooseDate = (raw: string): moment.Moment | null => {
    if (!raw) return null;
    if (raw.trim().length < 3) return null;
    const m = moment(raw, DATE_FORMATS, true);
    if (m.isValid()) return m;

    const m2 = moment(raw, undefined, true);
    if (m2.isValid()) return m2;

    const m3 = moment(raw);
    return m3.isValid() ? m3 : null;
};


type Token =
    | {
        field?: 'date' | 'net' | 'bank' | 'var';
        op?: '<' | '>' | '<=' | '>=' | '=' | '~';
        num?: number;
        date?: moment.Moment;
        range?: { from?: moment.Moment; to?: moment.Moment };
        text?: string;
        raw?: string;
    };

const parseQueryToTokens = (q?: string): Token[] => {
    if (!q) return [];
    const parts = q.trim().split(/\s+/);
    const tokens: Token[] = [];

    for (const p of parts) {
        const m = p.match(/^(?:(date|net|bank|var):)?\s*(<=|>=|=|<|>|~)?\s*(.+)$/i);
        if (!m) { tokens.push({ text: p, raw: p }); continue; }

        const [, fieldRaw, opRaw, valueRaw] = m;
        const field = fieldRaw?.toLowerCase() as Token['field'] | undefined;
        const op = opRaw as Token['op'] | undefined;
        const value = (valueRaw || '').trim();
        const raw = value;

        const rangeM = value.match(/^(.+)\.\.(.+)$/);
        if (rangeM) {
            const left = rangeM[1].trim();
            const right = rangeM[2].trim();
            const leftDate = parseLooseDate(left);
            const rightDate = parseLooseDate(right);
            if (leftDate || rightDate) {
                tokens.push({ field: field ?? 'date', range: { from: leftDate ?? undefined, to: rightDate ?? undefined }, raw });
                continue;
            }
            const leftNum = parseLooseNumber(left);
            const rightNum = parseLooseNumber(right);
            if (leftNum != null || rightNum != null) {
                tokens.push({ field, op: '~', text: `${left ?? ''}..${right ?? ''}`, raw });
                continue;
            }
            tokens.push({ field, text: value, raw });
            continue;
        }

        if (!field && !op && looksLikeLooseDigits(value)) {
            tokens.push({ text: value, raw });
            continue;
        }

        if (value.length >= 3) {
            const d = parseLooseDate(value);
            if (d && (field === 'date' || !field)) {
                tokens.push({ field: field ?? 'date', op: op ?? '=', date: d, raw });
                continue;
            }
        }

        const n = parseLooseNumber(value);
        if (n != null && (field === 'net' || field === 'bank' || field === 'var' || !field)) {
            tokens.push({ field, op: op ?? '=', num: n, raw, text: value });
            continue;
        }

        tokens.push({ field, text: value, raw });
    }
    return tokens;
};

const stringifyNumberVariants = (val: number): string[] => {
    const fixed2 = Number(val).toFixed(2);
    const noDec = String(Math.round(val));
    const asIs = String(val);
    const withCommas = Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return Array.from(new Set([fixed2, noDec, asIs, withCommas, withCommas.replace(/,/g, '')])).map(s => s.toLowerCase());
};
const textMatchesNumber = (val: number, needle: string) =>
    stringifyNumberVariants(val).some(s => s.includes(needle.toLowerCase()));

const numMatches = (val: number, token: any): boolean => {
    if (token.text?.includes('..')) {
        const [l, r] = token.text.split('..');
        const left = l ? Number(l) : null;
        const right = r ? Number(r) : null;
        if (left != null && right != null) return val >= left && val <= right;
        if (left != null) return val >= left;
        if (right != null) return val <= right;
        return true;
    }
    if (token.num == null || !token.op) return true;
    switch (token.op) {
        case '=': return val === token.num;
        case '<': return val < token.num;
        case '>': return val > token.num;
        case '<=': return val <= token.num;
        case '>=': return val >= token.num;
        default: return true;
    }
};

const dateMatches = (dateStr: string, token: any): boolean => {
    if (!dateStr) return false;
    if (dateStr === 'Total') return false;

    const rowDate = moment(dateStr);
    if (!rowDate.isValid()) return false;

    if (token?.range) {
        const okFrom = token?.range?.from ? rowDate.isSameOrAfter(token?.range?.from, 'day') : true;
        const okTo = token?.range?.to ? rowDate.isSameOrBefore(token?.range?.to, 'day') : true;
        return okFrom && okTo;
    }

    if (token?.date) {
        switch (token?.op) {
            case '<': return rowDate?.isBefore(token?.date, 'day');
            case '>': return rowDate?.isAfter(token?.date, 'day');
            case '<=': return rowDate?.isSameOrBefore(token?.date, 'day');
            case '>=': return rowDate?.isSameOrAfter(token?.date, 'day');
            case '=':
            default: return rowDate?.isSame(token?.date, 'day');
        }
    }

    if (token?.text) {
        const candidates = [
            rowDate.format('YYYY-MM-DD'),
            rowDate.format('DD-MM-YYYY'),
            rowDate.format('DD/MM/YYYY'),
            rowDate.format('MM/DD/YYYY'),
            rowDate.format('MMM D YYYY'),
            rowDate.format('MMMM D YYYY'),
            rowDate.format('MMM YYYY'),
            rowDate.format('MMMM YYYY'),
            rowDate.format('DD-MM'),
            rowDate.format('DD/MM'),
            rowDate.format('MM-YYYY'),
            rowDate.format('MM/YYYY'),
        ].map(s => s.toLowerCase());
        return candidates?.some(s => s.includes(token?.text!.toLowerCase()));
    }

    return true;
};

const matchRow = (row: any, tokens: Token[], type: any): boolean => {
    const fieldMap = {
        date: `paymentDate${type?.date}`,
        net: `${type?.value}Net`,
        bank: `${type?.value}Bank`,
        var: `${type?.value}Var`,
    };

    const isTotal = row?.[fieldMap.date] === 'Total';
    if (isTotal) return true;

    return tokens.every(tok => {
        const fieldsToTry: (keyof typeof fieldMap)[] =
            tok.field ? [tok.field] : (['date', 'net', 'bank', 'var'] as any);

        return fieldsToTry.some(f => {
            const key = fieldMap[f];
            const val = row?.[key];
            if (f === 'date') return dateMatches(String(val ?? ''), tok);
            const numVal = Number(val ?? 0);
            if (Number.isNaN(numVal)) return false;
            if (tok.text && !tok.op && tok.field !== 'date') return textMatchesNumber(numVal, tok.text);
            return numMatches(numVal, tok);
        });
    });
};

const BankReconTable: React.FC<BankReconTableProps> = ({
    rows,
    type,
    paginationData,
    paginationState,
    setPaginationState,
    showOnlyNonZeroVar,
    loading,
    searchQuery,
}) => {
    const showRows = !loading && rows && rows.length > 0;

    const tokens = useMemo(() => parseQueryToTokens(searchQuery), [searchQuery]);

    const filtered = useMemo(() => {
        if (!rows?.length) return [];
        const dateKey = `paymentDate${type?.date}`;
        const varKey = `${type?.value}Var`;

        const normalRows = rows.filter(r => r?.[dateKey] !== 'Total');
        const totalRow = rows.find(r => r?.[dateKey] === 'Total');

        let out = tokens.length ? normalRows.filter(r => matchRow(r, tokens, type)) : normalRows;

        if (showOnlyNonZeroVar) {
            out = out.filter(r => Number(r?.[varKey] ?? 0) !== 0);
        }

        if (totalRow) out.push(totalRow);
        return out;
    }, [rows, tokens, type, showOnlyNonZeroVar]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!paginationData || !setPaginationState || loading || !paginationData?.hasNextPage) return;

        const target = e.currentTarget;
        const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        const threshold = 40;

        if (distanceFromBottom <= threshold) {
            setPaginationState({
                ...paginationState,
                pageNumber: (paginationData.number || 1) + 1,
            });
        }
    };

    return (
        <div className=''>
            <div
                className='overflow-x-auto max-h-[520px] overflow-y-auto border border-1 !rounded-xl dark:border-borderColor-darkMode'
                onScroll={handleScroll}
            >
                <table className='min-w-full bg-white dark:bg-black dark:text-primary-dark'>
                    <thead className='bg-white dark:bg-black text-left text-sm font-medium text-black dark:text-primary-dark rounded-xl'>
                        <tr>
                            <th className='px-4 py-3'>Payment Date</th>
                            <th className='px-4 py-3'>{type?.name}(net)</th>
                            <th className='px-4 py-3'>Bank</th>
                            <th className='px-4 py-3'>Variance</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading && Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={`skeleton-${i}`} />)}

                        {showRows &&
                            filtered!.map((row: any) => (
                                <tr
                                    key={row.id} className={`hover:bg-gray-50 dark:hover:bg-bgColor-darkMode text-sm ${row?.[`paymentDate${type?.date}`] !== 'Total' ? 'border border-1' : ''}`} >
                                    <td className={`px-4 py-4 ${row?.[`paymentDate${type?.date}`] === 'Total' ? 'font-bold' : ''}`}>
                                        {row?.[`paymentDate${type?.date}`] === 'Total' ? 'Total' : moment(row?.[`paymentDate${type?.date}`]).format('DD-MM-YYYY')}
                                    </td>
                                    <td className='px-4 py-4'>{Number(row?.[`${type?.value}Net`] || 0).toFixed(2)}</td>
                                    <td className='px-4 py-4'>{Number(row?.[`${type?.value}Bank`] || 0).toFixed(2)}</td>
                                    <td className={`px-4 py-4 ${row?.[`paymentDate${type?.date}`] === 'Total' ? Number(row?.[`${type?.value}Var`] || 0) > 0 ? 'text-[#EF4444]' : 'text-[#10B981]' : ''}`}>{Number(row?.[`${type?.value}Var`] || 0).toFixed(2)}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BankReconTable;